import { FormEvent, useEffect, useRef, useState } from 'react';
import { Bot, CheckCircle2, Database, Loader2, LockKeyhole, Send, ShieldCheck, Sparkles, X } from 'lucide-react';
import type { AiAgentMessageDto, AiAgentStatusDto } from '../../models';
import { chatWithAiAgent, getAiAgentStatus, getApiError } from '../../services/api';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';

interface VisibleMessage extends AiAgentMessageDto {
  id: string;
  tools?: string[];
  local?: boolean;
}

const WELCOME: VisibleMessage = {
  id: 'welcome',
  role: 'assistant',
  local: true,
  content: 'Я могу найти терминал, сверить его связь и разобрать сохранённую диагностику. В этой версии я ничего не меняю.',
};

const SUGGESTIONS = [
  'Сколько терминалов сейчас не в сети?',
  'Покажи проблемные терминалы',
  'Проверь состояние telemed-1',
];

const TOOL_LABELS: Record<string, string> = {
  get_fleet_summary: 'Сводка парка',
  find_terminals: 'Поиск терминалов',
  get_terminal_health: 'Состояние терминала',
  run_terminal_diagnostics: 'Read-only диагностика',
};

export function AiAssistantDrawer({ open, onClose, nodeId }: { open: boolean; onClose: () => void; nodeId?: string }) {
  const [status, setStatus] = useState<AiAgentStatusDto>();
  const [messages, setMessages] = useState<VisibleMessage[]>([WELCOME]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    let active = true;
    getAiAgentStatus()
      .then((response) => active && setStatus(response.data.data))
      .catch((reason) => active && setError(getApiError(reason)));
    window.setTimeout(() => inputRef.current?.focus(), 100);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { active = false; document.body.style.overflow = previousOverflow; };
  }, [open]);

  useEffect(() => {
    if (open) scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, loading, open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, open]);

  if (!open) return null;

  const send = async (event?: FormEvent, suggested?: string) => {
    event?.preventDefault();
    const content = String(suggested ?? input).trim();
    if (!content || loading || !status?.enabled) return;
    const userMessage: VisibleMessage = { id: crypto.randomUUID(), role: 'user', content };
    const next = [...messages, userMessage];
    setMessages(next); setInput(''); setError(''); setLoading(true);
    try {
      const history = next.filter((message) => !message.local).map(({ role, content }) => ({ role, content }));
      const response = await chatWithAiAgent(history, nodeId);
      setMessages((current) => [...current, { id: crypto.randomUUID(), role: 'assistant', content: response.data.data.answer, tools: response.data.data.tools }]);
    } catch (reason) {
      setError(getApiError(reason));
    } finally {
      setLoading(false);
    }
  };

  return <div className="fixed inset-0 z-[70]" role="dialog" aria-modal="true" aria-labelledby="ai-assistant-title">
    <button className="absolute inset-0 cursor-default bg-slate-950/35 backdrop-blur-[2px]" onClick={onClose} aria-label="Закрыть ИИ-помощника" />
    <section className="absolute inset-y-0 right-0 flex w-full flex-col border-l border-slate-700/30 bg-white shadow-2xl sm:max-w-[620px]">
      <header className="shrink-0 bg-gradient-to-br from-slate-950 via-slate-900 to-cyan-950 px-5 py-5 text-white">
        <div className="flex items-start gap-3">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-cyan-300/20 bg-cyan-300/10 text-cyan-200"><Bot className="h-5 w-5" /></span>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h2 id="ai-assistant-title" className="text-lg font-semibold">ИИ-помощник</h2>
              <span className="rounded-full border border-emerald-300/20 bg-emerald-300/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-emerald-200">Только чтение</span>
            </div>
            <p className="mt-1 text-xs leading-5 text-slate-300">{nodeId ? 'Контекст: выбранный терминал' : 'Анализ парка и технического состояния'}</p>
          </div>
          <button onClick={onClose} className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-xl text-slate-300 transition-colors hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300" aria-label="Закрыть"><X className="h-5 w-5" /></button>
        </div>
        <div className="mt-4 flex flex-wrap gap-2 text-[11px] text-slate-300">
          <span className="inline-flex items-center gap-1.5 rounded-lg bg-white/5 px-2.5 py-1.5"><ShieldCheck className="h-3.5 w-3.5 text-emerald-300" />Без SSH и shell</span>
          <span className="inline-flex items-center gap-1.5 rounded-lg bg-white/5 px-2.5 py-1.5"><LockKeyhole className="h-3.5 w-3.5 text-cyan-300" />Ключ только на сервере</span>
          {status?.provider && <span className="inline-flex items-center gap-1.5 rounded-lg bg-white/5 px-2.5 py-1.5"><Database className="h-3.5 w-3.5" />{status.provider} · {status.model || 'без модели'}</span>}
        </div>
      </header>

      <div ref={scrollRef} className="min-h-0 flex-1 space-y-4 overflow-y-auto bg-slate-50 px-4 py-5 sm:px-6" aria-live="polite">
        {status && !status.enabled && <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950">
          <p className="font-semibold">Помощник пока не запущен</p>
          <p className="mt-1 leading-5 text-amber-800">{status.reason}</p>
          <p className="mt-2 font-mono text-[11px] text-amber-900">AI_AGENT_ENABLED=true · AI_AGENT_MODEL · AI_AGENT_API_KEY</p>
        </div>}
        {messages.map((message) => <div key={message.id} className={cn('flex', message.role === 'user' ? 'justify-end' : 'justify-start')}>
          <div className={cn('max-w-[88%] rounded-2xl px-4 py-3 text-sm leading-6 shadow-sm', message.role === 'user' ? 'rounded-br-md bg-cyan-700 text-white' : 'rounded-bl-md border border-slate-200 bg-white text-slate-800')}>
            <p className="whitespace-pre-wrap">{message.content}</p>
            {message.tools && message.tools.length > 0 && <div className="mt-3 flex flex-wrap gap-1.5 border-t border-slate-200 pt-2">
              {message.tools.map((tool, index) => <span key={`${tool}-${index}`} className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-2 py-1 text-[10px] font-semibold text-slate-600"><CheckCircle2 className="h-3 w-3 text-emerald-600" />{TOOL_LABELS[tool] || tool}</span>)}
            </div>}
          </div>
        </div>)}
        {loading && <div className="flex justify-start"><div className="inline-flex items-center gap-2 rounded-2xl rounded-bl-md border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600 shadow-sm"><Loader2 className="h-4 w-4 animate-spin text-cyan-700" />Проверяю данные…</div></div>}
        {error && <div role="alert" className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</div>}
      </div>

      <footer className="shrink-0 border-t border-slate-200 bg-white p-4 sm:p-5">
        {messages.length === 1 && <div className="mb-3 flex gap-2 overflow-x-auto pb-1">
          {SUGGESTIONS.map((suggestion) => <button key={suggestion} disabled={!status?.enabled} onClick={() => send(undefined, suggestion)} className="shrink-0 cursor-pointer rounded-full border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700 transition-colors hover:border-cyan-300 hover:bg-cyan-50 disabled:cursor-not-allowed disabled:opacity-50">{suggestion}</button>)}
        </div>}
        <form onSubmit={send} className="flex items-end gap-2">
          <label htmlFor="ai-assistant-message" className="sr-only">Вопрос ИИ-помощнику</label>
          <textarea ref={inputRef} id="ai-assistant-message" value={input} onChange={(event) => setInput(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter' && !event.shiftKey) { event.preventDefault(); send(); } }} disabled={!status?.enabled || loading} rows={2} maxLength={4000} placeholder="Например: почему telemed-24 требует внимания?" className="min-h-[48px] max-h-32 flex-1 resize-none rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-950 outline-none transition-colors placeholder:text-slate-400 focus:border-cyan-600 focus:ring-2 focus:ring-cyan-600/20 disabled:bg-slate-100" />
          <Button type="submit" size="icon" disabled={!status?.enabled || !input.trim() || loading} aria-label="Отправить"><Send className="h-4 w-4" /></Button>
        </form>
        <p className="mt-2 flex items-center gap-1.5 text-[10px] leading-4 text-slate-500"><Sparkles className="h-3 w-3" />Не вводите данные водителей, пароли, токены или полные конфиги.</p>
      </footer>
    </section>
  </div>;
}
